import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getWarning, getSummary } from '../lib/db.js';
import { CampLogo } from '../components/CampLogo.jsx';
import { COMPANY } from '../lib/company.js';
import { thaiDate } from '../lib/format.js';

// Print-friendly formal Thai warning letter. window.print() lets the user
// "Save as PDF". In production, Phase 2 generates this server-side (Edge
// Function) and stores it with a sha256 hash in hr.documents — this is the
// interim/in-browser version.
export function WarningLetter() {
  const { id } = useParams();
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [emp, setEmp] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const warn = await getWarning(id);
        if (!active) return;
        setW(warn);
        if (warn?.employee_id) {
          const s = await getSummary(warn.employee_id);
          if (active) setEmp(s);
        }
      } catch (e) {
        if (active) setErr(e.message);
      }
    })();
    return () => { active = false; };
  }, [id]);

  if (err) return <div className="letter-wrap"><p className="err">{err}</p></div>;
  if (!w) return <div className="center"><div className="spinner" /></div>;

  const name = emp ? `${emp.first_name} ${emp.last_name}${emp.nickname ? ` (${emp.nickname})` : ''}` : '—';
  const docDate = w.issued_at || w.incident_date;

  return (
    <div className="letter-wrap">
      <div className="letter-toolbar no-print">
        <button className="back" onClick={() => nav(-1)}>‹ กลับ</button>
        <button className="cta" onClick={() => window.print()}>พิมพ์ / บันทึก PDF</button>
      </div>

      <div className="letter">
        <div className="lh">
          <CampLogo height={34} style={{ color: '#16161a' }} />
          <div className="co-sub" style={{ marginTop: 8 }}>{COMPANY.name_en}</div>
          <div className="co-sub" style={{ fontSize: 11 }}>{COMPANY.address}</div>
          <div className="co-sub" style={{ fontSize: 11 }}>โทร {COMPANY.tel}</div>
        </div>

        <h2 className="lt">หนังสือตักเตือน</h2>
        <div className="lt-sub">ขั้นที่ {w.ladder_level} — {levelTh(w.level_name)} · มีผลถึง {thaiDate(w.expires_at)}</div>

        <div className="meta">
          <span>เลขที่ HR-WRN-{String(id).slice(-6).toUpperCase()}</span>
          <span>วันที่ {thaiDate(docDate)}</span>
        </div>

        <p><b>เรื่อง</b>&nbsp;&nbsp;ตักเตือนการฝ่าฝืนข้อบังคับเกี่ยวกับการทำงาน</p>
        <p><b>เรียน</b>&nbsp;&nbsp;คุณ {name}
          {emp?.position ? ` ตำแหน่ง ${emp.position}` : ''}{emp?.branch ? ` ${emp.branch}` : ''}</p>

        <p>
          ตามที่ท่านได้ {w.incident_detail || '—'} เมื่อวันที่ {thaiDate(w.incident_date)}{' '}
          อันเป็นการฝ่าฝืน<b>{w.policy_clause || 'ข้อบังคับเกี่ยวกับการทำงาน'}</b> ของบริษัทฯ นั้น
        </p>

        <p>
          บริษัทฯ จึงขอออกหนังสือตักเตือนฉบับนี้ (ขั้นที่ {w.ladder_level} — {levelTh(w.level_name)})
          เพื่อให้ท่านปรับปรุงแก้ไขและระมัดระวังมิให้เกิดเหตุการณ์ในลักษณะเดียวกันอีก
        </p>

        <p className="ref">
          หากท่านกระทำผิดซ้ำในเรื่องเดียวกัน <b>ภายในระยะเวลา 1 ปี</b> นับแต่วันที่เกิดเหตุ
          บริษัทฯ อาจพิจารณาดำเนินการทางวินัยในขั้นที่สูงขึ้น ซึ่งอาจรวมถึงการเลิกจ้าง
          ตามข้อบังคับและกฎหมายแรงงานที่เกี่ยวข้อง
        </p>

        <p>จึงเรียนมาเพื่อทราบและถือปฏิบัติ</p>

        <div className="sign">
          <div className="sig">
            <div className="sl"></div>
            <div className="cap">ผู้ออกหนังสือ ( {w.issued_by_name || '....................'} )</div>
          </div>
          <div className="sig">
            <div className="sl"></div>
            <div className="cap">พนักงานผู้รับทราบ ( {emp?.nickname || emp?.first_name || '....................'} )</div>
            <div className="chk">
              {w.ack_status === 'acknowledged' ? '☑' : '☐'} รับทราบ &nbsp;&nbsp;
              {w.ack_status === 'refused_with_witness' ? '☑' : '☐'} ปฏิเสธการลงนาม
            </div>
          </div>
          <div className="sig">
            <div className="sl"></div>
            <div className="cap">พยาน {w.witness_name ? `( ${w.witness_name} )` : ''}</div>
          </div>
          <div className="sig">
            <div className="sl"></div>
            <div className="cap">วันที่</div>
          </div>
        </div>

        <div className="seal" style={{ textAlign: 'center', borderTop: '1px solid #e2e2e6', paddingTop: 10 }}>
          {COMPANY.name_th} · {COMPANY.email} · {COMPANY.website}
        </div>
        <div className="seal">
          * แม่แบบนี้เป็นเอกสารตัวอย่าง ควรให้ทนายแรงงานตรวจก่อนใช้จริง ·
          ฉบับจริงในระบบจะลงลายมือชื่อดิจิทัล + เก็บ sha256 กันแก้ย้อนหลัง
        </div>
      </div>
    </div>
  );
}

const levelTh = (n) => ({ verbal: 'ตักเตือนด้วยวาจา', written: 'ลายลักษณ์อักษร', final: 'ครั้งสุดท้าย', terminate: 'พิจารณาเลิกจ้าง' }[n] || n || '');
